import { useAuth } from 'src/util/authenticationProvider';
import { useEffect, useState, useMemo, useCallback, useRef, type FormEvent } from 'react';
import axios from 'axios';
import { Alert, Box, Button, Container, CssBaseline, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Stack, TextField, ThemeProvider, Typography, useTheme } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import DeleteIcon from '@mui/icons-material/Delete';
import { PrizeDto, Lottery } from 'src/util/types';
import config from 'src/config';

type DateInputElement = HTMLInputElement & { showPicker?: () => void };

const formatDateForInput = (input: string | Date) => {
  const date = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(date.getTime())) {
    return typeof input === 'string' ? input : '';
  }
  const pad = (num: number) => num.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const normalizeInputValue = (value?: string) => {
  if (!value) return '';
  return value.includes('T') ? value.slice(0, 16) : value;
};

type PrizeSelection = {
  id: string;
  amount: number;
};

export default function LotteryEdit(props: Lottery & { onSuccess?: (() => void) }) {
  const authContext = useAuth();
  const theme = useTheme();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [prizeList, setPrizeList] = useState<PrizeDto[]>([]);
  const [selectedPrizes, setSelectedPrizes] = useState<PrizeSelection[]>([]);
  const [activationThreshold, setActivationThreshold] = useState<number>(props.activationThreshold ?? 0);
  const [startDate, setStartDate] = useState(normalizeInputValue(props.startDate));
  const [endDate, setEndDate] = useState(normalizeInputValue(props.endDate));
  const endDateInputRef = useRef<DateInputElement | null>(null);
  const prizeMap = useMemo(() => new Map(prizeList.map((p) => [p.id.toString(), p])), [prizeList]);
  const minStartDate = formatDateForInput(new Date());
  const minEndDate = useMemo(() => {
    const base = startDate ? new Date(startDate) : new Date();
    const min = new Date(base.getTime() + 60 * 1000);
    return formatDateForInput(min);
  }, [startDate]);

  const focusEndDateInput = useCallback(() => {
    requestAnimationFrame(() => {
      endDateInputRef.current?.focus();
      endDateInputRef.current?.showPicker?.();
    });
  }, [endDateInputRef]);
  const derivedSelections = useMemo(() => {
    if (!props?.prizes) return [];
    const rawWrappers = Array.isArray(props.prizes)
      ? props.prizes
      : Object.values(props.prizes);

    const aggregated = new Map<string, number>();
    rawWrappers.forEach((wrapper: any) => {
      const prizeId = wrapper?.prize?.id ?? wrapper?.id;
      if (!prizeId) return;
      const key = prizeId.toString();
      const amount = typeof wrapper?.amount === 'number' ? wrapper.amount : 1;
      aggregated.set(key, (aggregated.get(key) ?? 0) + amount);
    });

    return Array.from(aggregated.entries()).map(([id, amount]) => ({ id, amount }));
  }, [props.prizes]);

  useEffect(() => {
    setSelectedPrizes(derivedSelections);
  }, [derivedSelections]);

  useEffect(() => {
    setStartDate(normalizeInputValue(props.startDate));
  }, [props.startDate]);

  useEffect(() => {
    setEndDate(normalizeInputValue(props.endDate));
  }, [props.endDate]);

  useEffect(() => {
    setActivationThreshold(props.activationThreshold ?? 0);
  }, [props.activationThreshold]);

  const syncSelections = useCallback((ids: string[], current: PrizeSelection[]) => {
    const currentMap = new Map(current.map((entry) => [entry.id, entry]));
    return ids.map((id) => currentMap.get(id) ?? { id, amount: 1 });
  }, []);

  const handlePrizeSelectChange = useCallback((event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const ids = typeof value === 'string' ? value.split(',') : value;
    setSelectedPrizes((prev) => syncSelections(ids, prev));
  }, [syncSelections]);

  const handlePrizeAmountChange = useCallback((id: string, amount: number) => {
    setSelectedPrizes((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, amount: amount < 0 ? 0 : amount } : entry))
    );
  }, []);

  const handlePrizeDelete = useCallback((id: string) => {
    setSelectedPrizes((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const handleStartDateChange = useCallback((value: string) => {
    setStartDate(value);
    setEndDate('');
    focusEndDateInput();
  }, [focusEndDateInput]);

  const getPrizeList = async () => {
    try {
      const response = await axios.get(config.apiUrl + "/api/prize/all", {
        headers: {
          "Authorization": "Bearer " + authContext.tokens.accessToken
        }
      });
      const list: PrizeDto[] = response.data.content;
      setPrizeList(list);
      setSelectedPrizes((prev) => prev.filter((entry) => list.some((prize) => prize.id.toString() === entry.id)));
    } catch (err) {
      console.error(err);
    }
  };

  const validateDateRange = useCallback((startValue: string, endValue: string) => {
    if (!startValue || !endValue) {
      setError('Start and end dates are required');
      return false;
    }
    const startTime = new Date(startValue).getTime();
    const endTime = new Date(endValue).getTime();
    if (Number.isNaN(startTime) || Number.isNaN(endTime)) {
      setError('Invalid date selection');
      return false;
    }
    if (endTime <= startTime) {
      setError('End date must be after the start date');
      return false;
    }
    return true;
  }, [setError]);

  const handleSubmitData = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const formData = new FormData(event.currentTarget);
    const prizePayload = selectedPrizes.map((entry) => ({
      prize: { id: entry.id },
      amount: Number.isNaN(entry.amount) ? 0 : entry.amount
    }));
    const startDateValue = formData.get("startDate")?.toString() || props.startDate;
    const endDateValue = formData.get("endDate")?.toString() || props.endDate;
    if (!validateDateRange(startDateValue, endDateValue)) {
      return;
    }
    const bodyData = {
      id: props.id,
      startDate: startDateValue,
      endDate: endDateValue,
      name: formData.get("name") ? formData.get("name") : props.name,
      activationThreshold,
      prizes: prizePayload,
      default: props.default
    };
    axios.put(config.apiUrl + `/api/lottery/update/${props.id}`, bodyData, {
      headers: {
        'Authorization': 'Bearer ' + authContext.tokens.accessToken
      }
    })
      .then(() => {
        setSuccess('Speichern erfolgreich.');
        props.onSuccess?.();
      })
      .catch(() => {
        setError('Verbindung zum Server fehlgeschlagen.');
      });
  };

  useEffect(() => {
    getPrizeList();
    // eslint-disable-next-line
  }, [authContext.tokens?.accessToken]);

  return (
    <>
      <ThemeProvider theme={theme}>
        <Container component="main" maxWidth="lg">
          <CssBaseline />
          <Box
            sx={{
              marginTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box component="form" onSubmit={handleSubmitData} noValidate sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                fullWidth
                id="name"
                label="Name"
                name="name"
                defaultValue={props.name}
                autoComplete="name"
                autoFocus
              />
              <TextField
                margin="normal"
                type="datetime-local"
                required
                fullWidth
                id="startDate"
                name="startDate"
                value={startDate}
                onChange={(event) => handleStartDateChange(event.target.value)}
                inputProps={{ min: minStartDate }}
              />
              <TextField
                margin="normal"
                type="datetime-local"
                required
                fullWidth
                name="endDate"
                id="endDate"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                inputRef={endDateInputRef}
                inputProps={{ min: minEndDate }}
              />

              <TextField
                margin="normal"
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
                label="Activation Threshold"
                value={activationThreshold}
                onChange={(event) => setActivationThreshold(Number(event.target.value) || 0)}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel id="edit-prize-select-label">Select prizes</InputLabel>
                <Select
                  labelId="edit-prize-select-label"
                  id="edit-prize-select"
                  multiple
                  label="Select prizes"
                  value={selectedPrizes.map((entry) => entry.id)}
                  onChange={handlePrizeSelectChange}
                  renderValue={(selected) => (selected as string[])
                    .map((id) => prizeMap.get(id)?.description ?? '')
                    .filter(Boolean)
                    .join(', ')}
                >
                  {prizeList.map((prize) => (
                    <MenuItem key={prize.id} value={prize.id.toString()}>
                      {prize.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack spacing={2} sx={{ mt: 2 }}>
                {selectedPrizes.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No prizes selected yet.
                  </Typography>
                )}
                {selectedPrizes.map((entry) => {
                  const prize = prizeMap.get(entry.id);
                  return (
                    <Grid container spacing={2} alignItems="center" key={entry.id}>
                      <Grid item xs={6}>
                        <Typography>{prize?.description ?? entry.id}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          type="number"
                          fullWidth
                          label="Amount"
                          inputProps={{ min: 0 }}
                          value={entry.amount}
                          onChange={(event) => handlePrizeAmountChange(entry.id, Number(event.target.value) || 0)}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <IconButton aria-label="Delete" onClick={() => handlePrizeDelete(entry.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  );
                })}
              </Stack>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Speichern
              </Button>
            </Box>
            {error ? <Alert severity="error">{error}</Alert> : ''}
            {success ? <Alert severity="success">{success}</Alert> : ''}
          </Box>
        </Container>
      </ThemeProvider>
    </>
  );
}
